import { ConnectionStatus } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import {
  PayerMixDetail,
  RecommendedConnection,
  VerificationCountsByLocationIdsResponse,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { i18next } from '@frontend/i18n';

export const aggregateVerificationCounts = (data: VerificationCountsByLocationIdsResponse | undefined) => {
  const result = {
    'Auto-Verified': 0,
    Error: 0,
    'Manual Verified': 0,
    Total: 0,
    Unverified: 0,
  };

  const locations = data?.locationVerificationCounts || {};

  Object.values(locations).forEach((location) => {
    const analysis = location?.verificationAnalysis || {};
    result['Auto-Verified'] += analysis['Auto-Verified'] || 0;
    result['Error'] += analysis['Error'] || 0;
    result['Manual Verified'] += analysis['Manual Verified'] || 0;
    result['Total'] += analysis['Total'] || 0;
    result['Unverified'] += analysis['Unverified'] || 0;
  });

  return result;
};

/**
 * Returns a translated, human-readable label for a `ConnectionStatus` enum value.
 * Returns an empty string when the status is undefined or unrecognized so callers
 * can conditionally render UI (e.g. chips) based on presence.
 *
 * @param connectionStatus - The connection status enum value
 */
export const getConnectionStatusLabel = (connectionStatus: ConnectionStatus | undefined): string => {
  if (!connectionStatus) return '';

  const labelMap: Partial<Record<ConnectionStatus, string>> = {
    [ConnectionStatus.CONNECTION_STATUS_CONNECTED]: i18next.t('Connected'),
    [ConnectionStatus.CONNECTION_STATUS_DISCONNECTED]: i18next.t('Disconnected'),
    [ConnectionStatus.CONNECTION_STATUS_AUTH_FAILED]: i18next.t('Auth Failed'),
    [ConnectionStatus.CONNECTION_STATUS_NOT_CONNECTED]: i18next.t('Not Connected'),
    [ConnectionStatus.CONNECTION_STATUS_AUTO_MFA_NOT_SETUP]: i18next.t('Auto MFA Not Setup'),
    [ConnectionStatus.CONNECTION_STATUS_UNKNOWN]: i18next.t('Unknown'),
  };

  return labelMap[connectionStatus] ?? '';
};

export const getTopPayerMixDetailsFromRecommendations = (
  recommendedConnections: RecommendedConnection[] = [],
  payerMixDetails: PayerMixDetail[] = [],
  limit = 3
): PayerMixDetail[] => {
  if (!recommendedConnections.length || !payerMixDetails.length || limit <= 0) {
    return [];
  }

  const payerMixByPayerId = new Map(
    payerMixDetails
      .filter((payerMixRecord) => !!payerMixRecord.payerId)
      .map((payerMixRecord) => [payerMixRecord.payerId, payerMixRecord])
  );

  return recommendedConnections
    .map((connection) => (connection?.payerId ? payerMixByPayerId.get(connection.payerId) : undefined))
    .filter(
      (payerMixRecord): payerMixRecord is PayerMixDetail =>
        !!payerMixRecord && payerMixRecord.connectionStatus !== ConnectionStatus.CONNECTION_STATUS_UNKNOWN
    )
    .slice(0, limit);
};

export const TOP_PAYER_MIX_LIMIT = 3;
